import { ChevronDown, LogOut, ShieldCheck, User as UserIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { useLogout } from "@/features/auth/hooks";
import { useAuthStore } from "@/features/auth/store";
import type { MeResponse } from "@/features/auth/types";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/** Libellé du rôle affiché dans le menu. Priorité au rôle métier RBAC sur la
 *  clinique active ; repli sur le type de compte pour ceux qui n'en portent
 *  pas (superuser, éditeur, propriétaire). */
function roleLabel(me: MeResponse): string {
  if (me.is_superuser) return "Super-administrateur";
  if (me.is_platform_staff) return "Éditeur GoldenTech";
  const names = me.active_roles?.map((r) => r.name).filter(Boolean) ?? [];
  if (names.length) return names.join(", ");
  if (me.is_account_owner) return "Propriétaire du compte";
  return "Utilisateur";
}

export function UserMenu() {
  const me = useAuthStore((s) => s.me);
  const logout = useLogout();

  if (!me) return null;

  const initials = me.full_name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title={me.full_name}
          className="gap-2 px-1.5 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground sm:px-3"
        >
          <span className="grid h-7 w-7 place-items-center rounded-full bg-primary-foreground text-xs font-semibold text-primary">
            {initials}
          </span>
          {/* Prénom seulement à partir de `2xl` : en dessous, la navigation
              de bureau ou le nom de la clinique prennent la place (la barre
              débordait à 768 px) ; l'avatar reste, le nom est dans le menu. */}
          <span className="hidden text-sm font-medium 2xl:inline">{me.full_name.split(" ")[0]}</span>
          <ChevronDown className="hidden h-4 w-4 xl:block" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <span className="truncate text-sm font-semibold text-foreground">
              {me.full_name}
            </span>
            <span className="truncate text-xs text-muted">{me.email}</span>
            <span className="mt-1 inline-flex w-fit items-center gap-1.5 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{roleLabel(me)}</span>
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profil" className="flex items-center gap-2">
            <UserIcon className="h-4 w-4" /> Mon profil
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => logout.mutate()}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="h-4 w-4" /> Déconnexion
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
